import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import "@/styles/globals.css";
import "@/styles/module-kit.css";
import "@/styles/kit-chrome.css";
import "@/styles/native-forms.css";
import "@/styles/native-android.css";
import "@/styles/native-polish.css";
import "@/i18n"; // Initialize i18n before any component renders
import App from "@/App";
import { ErrorBoundary } from "@/components/error-boundary";
import { initSentry } from "@/lib/sentry";
import { installStaleChunkHandler } from "@/lib/stale-chunk";
import { queryClient } from "@/lib/query-client";
import { isAppLike, isPerfLite } from "@/lib/platform";
import { installToastDedupe, toasterLayout } from "@/lib/toast-config";
import { restoreNativeSessionMirror } from "@/store/auth-store";
import { restoreKioskCredMirror } from "@/lib/kiosk-session";
import { installGlobalCsrfHeader, installGlobalFetchCsrf } from "@/api/client";

declare global {
  interface Window {
    __CH_RELEASE_PREBOOT__?: () => void;
    /**
     * The route `scripts/prerender.mjs` generated THIS document for. Absent on
     * a dev-server document and on the pristine shell. See `ssgIsForThisRoute`.
     */
    __CH_SSG_PATH__?: string;
  }
}

initSentry();
installStaleChunkHandler();
installToastDedupe();
installGlobalCsrfHeader();
installGlobalFetchCsrf();

// Low-power visual mode for the native app (and reduce-motion users): flag it
// on <html> before first paint so ambient animations never render even one
// hot frame. globals.css keys its perf-lite overrides off this class.
if (isPerfLite()) {
  document.documentElement.classList.add("ch-perf-lite");
}

// Clean native/dev environment SW cache
if (("serviceWorker" in navigator) && (import.meta.env.DEV || isPerfLite() || /localhost/i.test(window.location.hostname))) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const reg of registrations) {
      reg.unregister().then((unregistered) => {
        if (unregistered) {
          console.log("[DEV/NATIVE] Stale service worker unregistered successfully.");
        }
      });
    }
  });
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element #root not found.");

let mounted = false;

function releasePreboot() {
  const release = window.__CH_RELEASE_PREBOOT__;
  if (typeof release === "function") {
    window.requestAnimationFrame(release);
    return;
  }

  document.documentElement.classList.remove("ch-preboot");
  document.documentElement.removeAttribute("data-ch-preboot-theme");
}

/**
 * Is the prerendered document in the page the one for this URL?
 *
 * Missing stamp = a document the prerenderer didn't write (the dev server, or
 * a build older than the stamp), and the old hydrate-anything behaviour is the
 * safer default there — a stale-but-correct page beats a blank one.
 */
/** The tab title every app route belongs under. */
const APP_TITLE = "CampusHubAI";

function ssgIsForThisRoute(): boolean {
  const generated = window.__CH_SSG_PATH__;
  if (typeof generated !== "string") return true;
  const norm = (p: string) => p.replace(/\/+$/, "") || "/";
  return norm(generated) === norm(window.location.pathname);
}

function mount() {
  // Idempotent: whichever of the two racers below wins, the tree mounts once.
  if (mounted) return;
  mounted = true;
  const toastLayout = toasterLayout(isAppLike());

  const tree = (
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Toaster
            position={toastLayout.position}
            visibleToasts={toastLayout.visibleToasts}
            offset={toastLayout.offset}
            mobileOffset={toastLayout.mobileOffset}
            toastOptions={{ className: "toast-item" }}
            // Web toasts wear the kit card (styles/kit-chrome.css: white card,
            // tone edge per type); the native app keeps sonner's rich colours.
            richColors={isAppLike()}
          />
          <App />
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );

  // `data-ssg="1"` is written by scripts/prerender.mjs, and only onto the
  // statically generated marketing pages. Hydrating those keeps the HTML the
  // crawler (and the visitor) already has on screen; client-rendering them
  // would blank the page for as long as the JS takes to parse.
  //
  // The marketing routes are React.lazy in router.tsx, so a boundary can
  // suspend mid-hydration. React's answer to that is to discard the server
  // HTML for that subtree and client-render it — i.e. exactly the old
  // behaviour, reported through onRecoverableError. So the worst case here is
  // what we had before, never a broken page.
  //
  // CAMP-2026-0579 — but only when the document IS the page being asked for.
  // `dist/index.html` is both the prerendered HOME page and the host's SPA
  // fallback, so opening /dashboard or /communications serves 33KB of landing
  // copy inside #root. Hydrating that against a completely different route
  // leaves the marketing markup on screen — unstyled, because site.css ships
  // with the marketing route chunk and is not loaded on an app route — until
  // React finishes discarding it. That is the "text-heavy content flashes
  // before the dashboard" in the report, on web and in the app alike.
  //
  // The prerenderer has always stamped which route each document is for;
  // nothing read it. When it doesn't match, the markup is wrong by definition:
  // drop it before it can be painted and client-render instead.
  if (rootElement!.dataset.ssg === "1" && !ssgIsForThisRoute()) {
    rootElement!.removeAttribute("data-ssg");
    rootElement!.replaceChildren();
    // CAMP-2026-0680 — the wrong document brought the wrong <head> with it.
    // scripts/prerender.mjs now corrects the title before first paint, but an
    // HTML shell cached from an older build won't carry that script, so the
    // reset is repeated here: a marketing tab title on /dashboard is what the
    // defect was actually about.
    document.title = APP_TITLE;
  }

  if (rootElement!.dataset.ssg === "1" && rootElement!.hasChildNodes()) {
    hydrateRoot(rootElement!, tree, {
      onRecoverableError: (error) => {
        // Not Sentry-worthy on its own: a mismatch costs a repaint, not
        // correctness, and the noisiest source is the lazy boundary above.
        if (import.meta.env.DEV) console.warn("[ssg] hydration fell back to client render", error);
      },
    });
    releasePreboot();
    return;
  }

  createRoot(rootElement!).render(tree);
  releasePreboot();
}

// On native, recover the session from Keychain / EncryptedSharedPreferences if
// the WebView dropped its localStorage mirror. This wants to finish before the
// first render, or the router evaluates an empty auth state and redirects to
// /login. No-op on web and whenever the mirror is already present, so it costs
// nothing on the normal path.
//
// NOTHING may block first paint indefinitely. Build 262 shipped a blank screen
// on every fresh install because this await never settled (a Capacitor plugin
// Proxy got assimilated as a thenable — see lib/secure-storage.ts), so
// `createRoot()` was never reached and there was no error, no boundary and no
// UI to recover from. The root cause is fixed, but gating the entire app on a
// pre-mount promise is a standing hazard, so the race below is permanent
// infrastructure: mount happens no later than MOUNT_DEADLINE_MS regardless of
// what the session probe does. A late session restore is a redirect to /login
// (recoverable); a missed mount is a dead app (not).
const MOUNT_DEADLINE_MS = 2500;

// CAMP-2026-0327 — the kiosk-credential mirror gets the same cold-start
// restore as the auth mirror. Without it an evicted localStorage made
// `hasKioskSession()` report false, and the first 401 of the day signed a gate
// tablet out instead of transparently re-logging it in. Both probes share the
// mount deadline; neither may ever block first paint.
void Promise.race([
  Promise.all([
    restoreNativeSessionMirror().catch(() => {}),
    restoreKioskCredMirror().catch(() => {}),
  ]).then(() => {}),
  new Promise<void>((resolve) => setTimeout(resolve, MOUNT_DEADLINE_MS)),
]).then(mount, mount);
